import React from "react";
import useDisplayMessage from "../../../common/lib/hooks/useDisplayMessage";
import useScaleWeight from "../../../common/lib/hooks/useScaleWeight";
import KaAlert from "../../../common/lib/KaAlert";
import ScaleReconnection from "../../../common/components/ScaleReconnection";
import Row from "../../../common/Row";
import { Units } from "../../../common/units/units";
import { Weight } from "../../../common/units/useWeight";
import Container, { Progress } from "../../../common/Container";
import Footer from "../../../common/Footer";
import FooterButton from "../../../common/FooterButton";
import { useLingui } from "@lingui/react";
import { msg, Trans } from "@lingui/macro";
import { driverFacingErrorMessage } from "../../../common/userFacingMessages/userFacingMessages";
import { unitsToDisplay } from "../../../common/units/unitsToDisplay";
import useReceivingAcceptTareWeight from "../../../requests/Receiving/useReceivingAcceptTareWeight";
import useReceivingBackToBayDirections from "../../../requests/Receiving/useReceivingBackToBayDirections";
import useReceivingAcceptManualWeight from "../../../requests/Receiving/useReceivingAcceptManualWeight";
import { useForm } from "react-hook-form";
import { convertWeight } from "../../../common/units/unitConversion";

export interface ReceivingTareWeightProps {
  siteId: string,
  scaleId: string,
  spoId: string,
  grossWeight?: Weight,
  devToolItems?: React.ReactNode,
  progress?: Progress,
  tareAccepted: () => void,
  back?: () => void,
  manualDeliveredAmountEntryRequired: boolean,
  officePhoneNumber?: string,
}

interface ManualDeliveredForm {
  manualDeliveredAmount: number,
  manualDeliveredUnit: Units
}

const ReceivingTareWeight = (props: ReceivingTareWeightProps) => {
  const { _ } = useLingui();
  const displayMessage = useDisplayMessage();

  const backToBayDirections = useReceivingBackToBayDirections(props.spoId, {
    onSuccess: () => props.back?.(),
    onError: err => displayMessage.fail(_(driverFacingErrorMessage(err))),
  });

  return (
    <Container
      title={_(msg`Verify Delivery`)}
      progress={props.progress}
      devToolItems={props.devToolItems}>
      <KaAlert
        displayMessage={displayMessage.message}
        onClose={displayMessage.clear} />
      {props.manualDeliveredAmountEntryRequired
        ? <ManualDeliveredContent
          displayMessage={displayMessage}
          backToBayDirections={backToBayDirections}
          spoId={props.spoId}
          tareAccepted={props.tareAccepted}
          allowBack={!!props.back}
        />
        : <ScaleDrivenContent
          displayMessage={displayMessage}
          backToBayDirections={backToBayDirections}
          spoId={props.spoId}
          siteId={props.siteId}
          scaleId={props.scaleId}
          grossWeight={props.grossWeight}
          officePhoneNumber={props.officePhoneNumber}
          tareAccepted={props.tareAccepted}
          allowBack={!!props.back}
        />
      }
    </Container>
  );
};

interface ManualDeliveredContentProps {
  displayMessage: any;
  backToBayDirections: any;
  spoId: string;
  tareAccepted: () => void;
  allowBack?: boolean;
}

const ManualDeliveredContent: React.FC<ManualDeliveredContentProps> = (props) => {
  const { _ } = useLingui();

  const submitManualDeliveredAmount = useReceivingAcceptManualWeight(props.spoId, {
    onSuccess: () => props.tareAccepted(),
    onError: err => props.displayMessage.fail(_(driverFacingErrorMessage(err))),
  });

  const { register, watch, formState: { errors } } = useForm<ManualDeliveredForm>({
    defaultValues: {
      manualDeliveredAmount: 0,
      manualDeliveredUnit: Units.lb
    }
  });

  const manualAmount = watch('manualDeliveredAmount');
  const manualUnit = watch('manualDeliveredUnit');

  return <>
    <div className="m-auto w-75">
      <Row>
        <div className="col-10 mt-5 center">
          <label className="h5" htmlFor='manualDeliveredAmount'><Trans>Delivered Amount</Trans></label>
          <div className="input-group mt-1">
            <input
              id='manualDeliveredAmount'
              type='text'
              className={`form-control ${!!errors.manualDeliveredAmount && 'is-invalid'}`}
              {...register(`manualDeliveredAmount` as const, {
                valueAsNumber: true,
                required: _(msg`Delivered amount required`),
                validate: {
                  positive: (v: number) => v > 0 || _(msg`Must be greater than 0`),
                },
              })}
            />
            <div className="input-group-append">
              <select
                id="manualDeliveredUnit"
                className="form-select"
                {...register("manualDeliveredUnit", {
                  required: true
                })} >
                <option className='dropdown-item' value={Units.lb}>{_(unitsToDisplay(Units.lb))}</option>
                <option className='dropdown-item' value={Units.kg}>{_(unitsToDisplay(Units.kg))}</option>
                <option className='dropdown-item' value={Units.oz}>{_(unitsToDisplay(Units.oz))}</option>
                <option className='dropdown-item' value={Units.ton}>{_(unitsToDisplay(Units.ton))}</option>
                <option className='dropdown-item' value={Units.t}>{_(unitsToDisplay(Units.t))}</option>
              </select>
            </div>
          </div>
        </div>
      </Row>
    </div>
    <Footer
      backButton={props.allowBack &&
        <FooterButton
          spinning={props.backToBayDirections.isLoading || submitManualDeliveredAmount.isLoading}
          onClick={() => props.backToBayDirections.request()}>
          <Trans>Back</Trans>
        </FooterButton>
      }
      nextButton={
        <FooterButton
          spinning={props.backToBayDirections.isLoading || submitManualDeliveredAmount.isLoading}
          onClick={() => submitManualDeliveredAmount.request({
            ManualDeliveredAmount: manualAmount,
            ManualDeliveredUnit: manualUnit
          })}
          disabled={!manualAmount || manualAmount <= 0}>
          <Trans>Accept Delivery</Trans>
        </FooterButton>
      }
    />
  </>
};

interface ScaleDrivenContentProps {
  displayMessage: any;
  backToBayDirections: any;
  spoId: string;
  siteId: string;
  scaleId: string;
  grossWeight?: Weight;
  officePhoneNumber?: string;
  tareAccepted: () => void;
  allowBack?: boolean;
}

const ScaleDrivenContent: React.FC<ScaleDrivenContentProps> = (props) => {
  const { _ } = useLingui();

  const acceptTare = useReceivingAcceptTareWeight(props.spoId, {
    onSuccess: () => props.tareAccepted(),
    onError: err => props.displayMessage.fail(_(driverFacingErrorMessage(err))),
  });

  const { weight, isReconnecting, showEscalationMessage } = useScaleWeight({
    siteId: props.siteId,
    scaleId: props.scaleId
  });

  const grossWeightValue = props.grossWeight ? convertWeight(props.grossWeight, Units.lb) : 0;
  const deliveredWeight = (!weight.value || weight.value <= 0) ? 0 : grossWeightValue - convertWeight(weight, Units.lb);

  return <>
    <div className="m-auto w-75">
      {isReconnecting
        ? <ScaleReconnection
          showEscalationMessage={showEscalationMessage}
          officePhoneNumber={props.officePhoneNumber} />
        : <>
          <Row>
            <div className="col-7 mt-5">
              <label className="h5" htmlFor='tareWeight'><Trans>Tare Weight</Trans></label>
              <div className="input-group input-group-sm mt-1">
                <input
                  id='tareWeight'
                  type='text'
                  className='form-control form-control-sm p-2 text-end'
                  value={weight.value}
                  readOnly />
                <div className="input-group-append input-group-text">
                  {_(unitsToDisplay(weight.unit))}
                </div>
              </div>
            </div>
            <div className="col-5 mt-5 text-end px-0">
              <div>
                <h5><Trans>Delivered Amount</Trans></h5>
              </div>
              <div>
                <label>
                  {_(msg`${deliveredWeight > 0 ? deliveredWeight : 0} lb`)}
                </label>
              </div>
            </div>
          </Row>
          <Row>
            <div className="col-7" />
            <div className="col-5 mt-5 text-end px-0">
              <div>
                <h5><Trans>Gross Weight</Trans></h5>
              </div>
              <div>
                <label>
                  {_(msg`${grossWeightValue} lb`)}
                </label>
              </div>
            </div>
          </Row>
        </>
      }
    </div>
    <Footer
      backButton={props.allowBack &&
        <FooterButton
          spinning={props.backToBayDirections.isLoading || acceptTare.isLoading}
          onClick={() => props.backToBayDirections.request()}>
          <Trans>Back</Trans>
        </FooterButton>
      }
      nextButton={
        <FooterButton
          spinning={props.backToBayDirections.isLoading || acceptTare.isLoading}
          onClick={() => acceptTare.request()}
          disabled={isReconnecting || deliveredWeight <= 0}>
          <Trans>Accept Delivery</Trans>
        </FooterButton>
      }
    />
  </>
};


export default ReceivingTareWeight;
